import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import urls from "../../Utils/urls";
import { allBlog, blogDetail } from "../../Utils/api";
import BlogImage from "./BlogImage";
import BlogCard from "./BlogCard";
import {
  excerpt,
  formatBlogDate,
  normalizeBlogHtml,
  readingTime,
} from "./blogUtils";
import "../../css/Common.css";
import "./BlogDetail.css";

const IcoClock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IcoCalendar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IcoWhatsApp = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const IcoLinkedIn = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8.02h4.56V24H.22zM8.34 8.02h4.37v2.18h.06c.61-1.15 2.1-2.36 4.32-2.36 4.62 0 5.47 3.04 5.47 7v9.16h-4.56v-8.12c0-1.94-.03-4.43-2.7-4.43-2.7 0-3.12 2.11-3.12 4.29V24H8.34z" />
  </svg>
);

const IcoX = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.46l8.6-9.83L0 1.15h7.59l5.24 6.93zm-1.29 19.5h2.04L6.48 3.24H4.29z" />
  </svg>
);

const IcoLink = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const BlogDetail = () => {
  const { id: slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | missing | error
  const [related, setRelated] = useState([]);
  const [category, setCategory] = useState("");
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState(0);
  const articleRef = useRef(null);

  // ── Article ──
  useEffect(() => {
    let active = true;
    setStatus("loading");
    setBlog(null);
    window.scrollTo(0, 0);

    blogDetail({ blog_slug: slug })
      .then((resp) => {
        if (!active) return;
        if (resp?.data?.Status === 1 && resp.data.data?.length) {
          setBlog(resp.data.data[0]);
          setStatus("ready");
        } else if (resp?.data?.Status === 0) {
          setStatus("missing");
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        if (active) setStatus("error");
      });

    return () => {
      active = false;
    };
  }, [slug]);

  // ── Related posts (also the only source of this post's category) ──
  useEffect(() => {
    let active = true;

    allBlog({ start: "0", end: "100" })
      .then((resp) => {
        if (!active || resp?.data?.Status !== 1) return;
        const list = resp.data.data || [];
        const self = list.find((item) => item.slug === slug);
        if (self?.category_name) setCategory(self.category_name);

        const others = list.filter((item) => item.slug !== slug);
        const sameCategory = self?.category_name
          ? others.filter((item) => item.category_name === self.category_name)
          : [];
        const pool = sameCategory.length >= 3 ? sameCategory : others;
        setRelated(pool.slice(0, 3));
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [slug]);

  // ── Reading progress ──
  useEffect(() => {
    if (status !== "ready") return undefined;

    const onScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const top = el.offsetTop;
      const scrollable = el.offsetHeight - window.innerHeight;
      if (scrollable <= 0) {
        setProgress(window.scrollY > top ? 100 : 0);
        return;
      }
      const ratio = (window.scrollY - top) / scrollable;
      setProgress(Math.min(100, Math.max(0, ratio * 100)));
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [status]);

  const { html, headings } = useMemo(
    () => normalizeBlogHtml(blog?.long_description, { dropTitle: blog?.title || "" }),
    [blog]
  );

  const shareUrl = `${urls.SITE_URL}/blog/${slug}`;
  const date = formatBlogDate(blog?.publish_date || blog?.created_date);
  const minutes = readingTime(blog?.long_description);
  const metaDescription =
    blog?.seo_description || excerpt(blog?.short_description, 155) || "";

  const copyLink = () => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).then(done).catch(() => {});
      return;
    }
    // Older Safari / non-secure origins have no async clipboard API.
    const field = document.createElement("textarea");
    field.value = shareUrl;
    field.setAttribute("readonly", "");
    field.style.position = "absolute";
    field.style.left = "-9999px";
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand("copy");
      done();
    } catch (e) {
      /* clipboard unavailable -- the URL is still in the address bar */
    }
    document.body.removeChild(field);
  };

  // ── Loading / failure states ──
  if (status === "loading") {
    return (
      <div className="bd-page">
        <div className="bd-hero">
          <div className="bd-hero-inner">
            <span className="bd-sk bd-sk-line" style={{ width: "30%" }} />
            <span className="bd-sk bd-sk-title" />
            <span className="bd-sk bd-sk-title" style={{ width: "65%" }} />
            <span className="bd-sk bd-sk-line" style={{ width: "40%" }} />
          </div>
        </div>
        <div className="bd-body">
          <div className="bd-body-inner">
            <div className="bd-article">
              <span className="bd-sk bd-sk-banner" />
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="bd-sk bd-sk-line" style={{ width: i % 3 === 2 ? "72%" : "100%" }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status !== "ready") {
    const missing = status === "missing";
    return (
      <div className="bd-page">
        <div className="bd-hero">
          <div className="bd-hero-inner bd-hero-inner--center">
            <span className="bd-tag">{missing ? "404" : "Error"}</span>
            <h1>{missing ? "This article isn't available" : "We couldn't load this article"}</h1>
            <p>
              {missing
                ? "The link may be outdated or the post has been moved. Browse the rest of our articles instead."
                : "Something went wrong on our end. Please try again in a moment."}
            </p>
            <Link to="/blog" className="bd-btn">Back to all blogs</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Article ──
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description: metaDescription,
    datePublished: blog.publish_date || blog.created_date,
    dateModified: blog.created_date || blog.publish_date,
    mainEntityOfPage: { "@type": "WebPage", "@id": shareUrl },
    author: { "@type": "Organization", name: "Salary Uncle" },
    publisher: { "@type": "Organization", name: "Salary Uncle" },
  };

  return (
    <div className="bd-page">
      <Helmet>
        <title>{blog.seo_title || `${blog.title} | Salary Uncle`}</title>
        <meta name="description" content={metaDescription} />
        {blog.seo_keyword && <meta name="keywords" content={blog.seo_keyword} />}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.seo_title || blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.seo_title || blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={shareUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bd-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      {/* ── Hero ── */}
      <header className="bd-hero">
        <div className="bd-hero-inner">
          <nav className="bd-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/blog">Blog</Link>
          </nav>

          {category && <span className="bd-tag">{category}</span>}
          <h1>{blog.title}</h1>

          <div className="bd-meta">
            {date && (
              <span className="bd-meta-item"><IcoCalendar /> {date}</span>
            )}
            <span className="bd-meta-item"><IcoClock /> {minutes} min read</span>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="bd-body">
        <div className="bd-body-inner">
          <article className="bd-article" ref={articleRef}>
            <div className="bd-banner">
              <BlogImage
                src={blog.banner_image_url || blog.thumb_image_url}
                alt={blog.title}
                className="bd-banner-img"
                eager
              />
            </div>

            {blog.short_description && (
              <p className="bd-standfirst">{blog.short_description}</p>
            )}

            {/* Inline TOC for small screens, where the sidebar is stacked below. */}
            {headings.length > 2 && (
              <details className="bd-toc-inline">
                <summary>In this article</summary>
                <ol>
                  {headings.map((item) => (
                    <li key={item.id} data-level={item.level}>
                      <a href={`#${item.id}`}>{item.text}</a>
                    </li>
                  ))}
                </ol>
              </details>
            )}

            <div
              className="bd-content"
              dangerouslySetInnerHTML={{ __html: html }}
            />

            {/* ── Share ── */}
            <div className="bd-share">
              <span className="bd-share-label">Share this article</span>
              <div className="bd-share-btns">
                <a
                  className="bd-share-btn bd-share-btn--wa"
                  href={`https://wa.me/?text=${encodeURIComponent(`${blog.title} ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on WhatsApp"
                >
                  <IcoWhatsApp />
                </a>
                <a
                  className="bd-share-btn bd-share-btn--x"
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blog.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on X"
                >
                  <IcoX />
                </a>
                <a
                  className="bd-share-btn bd-share-btn--li"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share on LinkedIn"
                >
                  <IcoLinkedIn />
                </a>
                <button
                  type="button"
                  className="bd-share-btn bd-share-btn--copy"
                  onClick={copyLink}
                  aria-label="Copy link"
                >
                  <IcoLink />
                </button>
                {copied && <span className="bd-copied" role="status">Link copied</span>}
              </div>
            </div>
          </article>

          {/* ── Sidebar ── */}
          <aside className="bd-side">
            {headings.length > 2 && (
              <nav className="bd-toc" aria-label="Table of contents">
                <h2>In this article</h2>
                <ol>
                  {headings.map((item) => (
                    <li key={item.id} data-level={item.level}>
                      <a href={`#${item.id}`}>{item.text}</a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <div className="bd-cta">
              <span className="bd-cta-tag">Need funds today?</span>
              <h2>Get a salary advance in minutes</h2>
              <p>100% online, minimal documents and money straight to your bank account.</p>
              <Link to="/apply-now" className="bd-cta-btn">Check eligibility</Link>
              <Link to="/calculator" className="bd-cta-link">Calculate your EMI</Link>
            </div>
          </aside>
        </div>

        {/* ── Related ── */}
        {related.length > 0 && (
          <section className="bd-related">
            <div className="bd-related-inner">
              <div className="bd-related-head">
                <h2>Keep reading</h2>
                <Link to="/blog" className="bd-related-all">View all articles</Link>
              </div>
              <div className="bd-related-grid">
                {related.map((item) => (
                  <BlogCard key={item.id || item.slug} blog={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
